import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';
const API_BASE = RAW_BASE.replace(/["']/g, '').replace(/\/+$/, '');

export default function StudentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('Student');

  const fetchAssignedExams = useCallback(async () => {
    setLoading(true);
    setError('');
    setExams([]);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const studentId = user?.id || user?.student_id;

      // ✅ FIX: Cache-busting timestamp + no-cache headers so we ALWAYS get fresh statuses
      const res = await fetch(`${API_BASE}/student/assignments?studentId=${studentId}&_t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
        return;
      }
      
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      
      const data = await res.json();

      // Filter out completed exams
      const activeExams = data.filter(exam => {
        const status = exam.status?.toString().trim().toLowerCase();
        return status !== 'completed';
      });
      
      setExams(activeExams);
    } catch (err) {
      console.error('❌ Failed to fetch exams:', err);
      setError(`Unable to load exams: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setFirstName(user?.firstName || user?.name || user?.username || 'Student');
    fetchAssignedExams();
  }, [location.pathname, fetchAssignedExams]);

  useEffect(() => {
    const onFocus = () => fetchAssignedExams();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchAssignedExams]);

  // ✅ FIX: Listen for refresh events dispatched by exam players when an exam finishes
  useEffect(() => {
    const handleRefresh = () => {
      console.log('🔄 Dashboard refresh triggered by completed exam');
      fetchAssignedExams();
    };
    window.addEventListener('dashboard_refresh', handleRefresh);
    return () => window.removeEventListener('dashboard_refresh', handleRefresh);
  }, [fetchAssignedExams]);

  // ✅ FIX: Robust routing logic for Math Diagnostic vs Standard Exams
  const handleStartExam = (exam) => {
    const { id: examId, assignment_id, exam_topic, diagnostic_config, start_band, end_band } = exam;
    
    if (!examId) return;
    
    const targetAssignmentId = assignment_id || examId;
    
    const isMath = exam_topic === 'math';
    const isFlow = start_band && end_band;
    // If diagnostic_config is present OR it's math and not a flow exam, treat as diagnostic
    const isDiagnostic = !!diagnostic_config || (isMath && !isFlow);

    if (isMath && isDiagnostic) {
      navigate(`/student/math-exam/${examId}?assignmentId=${targetAssignmentId}`);
    } else {
      navigate(`/student/start-exam/${examId}?assignmentId=${targetAssignmentId}`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('studentId');
    navigate('/login');
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 80, color: '#666' }}>⏳ Loading your assessments...</div>;
  if (error) return <div style={{ textAlign: 'center', marginTop: 80, color: '#dc3545' }}>❌ {error}<br/><button onClick={fetchAssignedExams} style={{marginTop:10, padding:'8px 16px', background:'#007BFF', color:'#fff', border:'none', borderRadius:6, cursor:'pointer'}}>Retry</button></div>;

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>Hello, {firstName}! 👋</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={fetchAssignedExams} style={{ padding: '8px 14px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>🔄 Refresh</button>
          <button onClick={handleLogout} style={{ padding: '8px 14px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Logout</button>
        </div>
      </div>

      <p style={{ textAlign: 'center', color: '#666', marginBottom: 30 }}>Select an assessment to begin.</p>

      {exams.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, background: '#f8f9fa', borderRadius: 8, border: '1px dashed #ccc' }}>
          🎉 No pending exams. Great job staying on track!
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {exams.map(exam => {
            const isFlow = exam.start_band && exam.end_band;
            const isMathDiagnostic = exam.exam_topic === 'math' && (exam.diagnostic_config || !isFlow);
            const isInProgress = exam.status?.toLowerCase() === 'in_progress';
            
            return (
              <div key={exam.id} style={{ 
                padding: 18, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: 17 }}>{exam.exam_name}</h3>
                  <p style={{ margin: 0, color: '#888', fontSize: 13 }}>
                    {exam.exam_topic?.toUpperCase()} | 
                    {isMathDiagnostic ? '  Math Diagnostic' : isFlow ? ` Flow: ${exam.start_band} → ${exam.end_band}` : ' Standard Exam'}
                    {isInProgress && <span style={{ marginLeft: 8, color: '#007BFF', fontWeight: 500 }}>• In Progress</span>}
                  </p>
                </div>
                <button
                  onClick={() => handleStartExam(exam)}
                  style={{ 
                    padding: '10px 22px', fontSize: 15, 
                    background: isInProgress ? '#ffc107' : (isMathDiagnostic ? '#28a745' : '#007BFF'), 
                    color: '#fff', 
                    border: 'none', borderRadius: 8, 
                    cursor: 'pointer', fontWeight: 500, 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                >
                  {isInProgress ? '🔄 Resume' : isMathDiagnostic ? '🧮 Start Diagnostic' : 'Start Exam'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}